/**
 * Text Uploader — spec FR-002
 * Accepts TXT, DOCX, PDF, direct paste; shows word count.
 */

import { useState, useCallback } from "react";
import { FileUploader } from "./FileUploader";
import { uploadFiles, type UploadProgress, formatFileSize } from "@/lib/storage/upload";

interface TextUploaderProps {
  projectId: string;
  onUploadComplete: (storagePaths: string[]) => void;
  onError?: (error: string) => void;
}

const TEXT_ACCEPT = ".txt,.docx,.pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf";

export function TextUploader({ projectId, onUploadComplete, onError }: TextUploaderProps) {
  const [progress, setProgress] = useState<UploadProgress[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [pasteText, setPasteText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState<"file" | "paste">("file");

  const handleFilesSelected = useCallback((selectedFiles: File[]) => {
    setFiles(selectedFiles);
    setProgress(
      selectedFiles.map((f) => ({
        file_name: f.name,
        bytes_uploaded: 0,
        total_bytes: f.size,
        percent: 0,
        status: "preparing" as const,
      })),
    );
  }, []);

  const handleUpload = useCallback(async () => {
    if (uploading) return;

    if (mode === "paste" && pasteText.trim()) {
      // Convert pasted text to a file and upload
      const blob = new Blob([pasteText], { type: "text/plain" });
      const file = new File([blob], "pasted-text.txt", { type: "text/plain" });
      setUploading(true);
      try {
        const paths = await uploadFiles([file], projectId, "text", (_, fileProgress) => {
          setProgress([fileProgress]);
        });
        onUploadComplete(paths);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Could not add pasted source.";
        onError?.(message);
      } finally {
        setUploading(false);
      }
      return;
    }

    if (files.length === 0) return;

    setUploading(true);
    try {
      const paths = await uploadFiles(files, projectId, "text", (fileIndex, fileProgress) => {
        setProgress((prev) => {
          const next = [...prev];
          next[fileIndex] = fileProgress;
          return next;
        });
      });
      onUploadComplete(paths);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not add manuscript source.";
      onError?.(message);
    } finally {
      setUploading(false);
    }
  }, [files, pasteText, projectId, mode, uploading, onUploadComplete, onError]);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const pasteWordCount = pasteText.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Mode toggle — mono caps */}
      <div className="flex gap-2">
        <ModeChip active={mode === "file"} onClick={() => setMode("file")}>Add files</ModeChip>
        <ModeChip active={mode === "paste"} onClick={() => setMode("paste")}>Paste pages</ModeChip>
      </div>

      {mode === "file" ? (
        <>
          <FileUploader
            accept={TEXT_ACCEPT}
            fileType="text"
            onFilesSelected={handleFilesSelected}
            uploadProgress={progress}
            label="Drop manuscript pages here, or browse"
            description="TXT, DOCX, PDF - up to 200,000 words"
          />
          {files.length > 0 && (
            <div className="flex flex-col justify-between gap-3 rounded-as-2 bg-paper-2 px-3 py-3 sm:flex-row sm:items-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
                {files.length} file{files.length > 1 ? "s" : ""} · {formatFileSize(totalSize)}
              </div>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="inline-flex items-center justify-center rounded-as-2 bg-ink-1 px-[15px] py-[8px] font-ui text-t-14 font-semibold text-paper-raised shadow-as-1 transition-colors duration-as-1 hover:bg-walnut-deep disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploading ? "Adding source..." : "Add pages to the source"}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste the pages, notes, outline, or draft here..."
            className="h-48 w-full resize-y rounded-as-3 border border-rule bg-paper-raised p-4 font-text text-t-16 leading-as-read text-ink-2 shadow-as-1 placeholder:text-ink-4 focus:border-walnut focus:outline-none"
          />
          {pasteText.trim() && (
            <div className="flex flex-col justify-between gap-3 rounded-as-2 bg-paper-2 px-3 py-3 sm:flex-row sm:items-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
                {pasteWordCount.toLocaleString()} words
              </div>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="inline-flex items-center justify-center rounded-as-2 bg-ink-1 px-[15px] py-[8px] font-ui text-t-14 font-semibold text-paper-raised shadow-as-1 transition-colors duration-as-1 hover:bg-walnut-deep disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploading ? "Adding source..." : "Add pasted pages"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ModeChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        "rounded-as-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-as-1 " +
        (active
          ? "bg-walnut-tint text-walnut-deep"
          : "bg-paper-raised text-ink-3 hover:bg-paper-2")
      }
    >
      {children}
    </button>
  );
}
